import { getApiData, postFormData } from "./APIHelper";
import { Setting } from "./Setting";
import { store } from "../redux/store/configureStore";
import { getContractorId } from "./tokenUtils";

function getUserToken() {
  const {
    auth: { token },
  } = store.getState();
  return `Bearer ${token}`;
}

/**
 * Download roster workers as CSV file
 * @param {number} rosterId - The roster ID
 * @returns {Promise} Blob data for CSV file
 */
export const downloadRosterCSV = async (rosterId) => {
  try {
    const token = getUserToken();
    const url = `${Setting.newBaseUrl}rosters/${rosterId}/workers/download`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: token,
      },
    });

    if (!response.ok) {
      // If the download endpoint doesn't exist, throw an error
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to download CSV");
    }

    // Return the blob for file download
    const blob = await response.blob();
    return blob;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new project roster
 * @param {number} projectId - The project ID
 * @param {string} rosterName - Name of the roster
 * @returns {Promise} API response
 */
export const createProjectRoster = async (projectId, rosterName) => {
  try {
    // Get contractor ID from token
    const { auth: { token } } = store.getState();
    const contractorId = getContractorId(token);

    const response = await getApiData(
      `api/roster/create`,
      "POST",
      { project_id: projectId, roster_name: rosterName, contractor_id: contractorId },
      { "Content-Type": "application/json" },
      false // Use REACT_APP_BASE_URL (Node.js API)
    );

    // HACK: Immediately update roster to ensure contractorId is saved
    // Backend create endpoint doesn't always save contractorId properly
    const rosterId = response?.data?.id || response?.id || response?.roster?.id || response?.data?.roster?.id;
    
    if (rosterId) {
      try {
        await getApiData(
          `projects/rosters/${rosterId}`,
          "PUT",
          { rosterName, contractorId },
          { "Content-Type": "application/json" },
          true // Use REACT_APP_NEW_BASE_URL
        );
      } catch (updateError) {
        // Don't throw - roster was created successfully, just contractorId update failed
      }
    }

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get rosters by project ID
 * @param {number} projectId - The project ID
 * @returns {Promise} API response with array of rosters
 */
export const getRostersByProjectId = async (projectId) => {
  try {
    const response = await getApiData(
      `projects/${projectId}/rosters`,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get roster details by roster ID
 * @param {number} rosterId - The roster ID
 * @returns {Promise} API response with roster details
 */
export const getRosterDetails = async (rosterId) => {
  try {
    const response = await getApiData(
      `projects/rosters/${rosterId}`,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Upload workers via CSV file
 * @param {number} rosterId - The roster ID
 * @param {File} file - CSV file to upload
 * @returns {Promise} API response
 */
export const uploadWorkersCSV = async (rosterId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await postFormData(
      `rosters/${rosterId}/workers/upload`,
      formData,
      {}, // No additional headers needed, FormData sets Content-Type automatically
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Add worker(s) to roster manually
 * @param {number} rosterId - The roster ID
 * @param {Array} workers - Array of worker objects
 * @returns {Promise} API response
 */
export const addWorkersToRoster = async (rosterId, workers) => {
  try {
    const response = await getApiData(
      `projects/rosters/${rosterId}/workers`,
      "POST",
      workers,
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update worker in roster
 * @param {number} rosterId - The roster ID
 * @param {number} workerId - The worker ID to update
 * @param {Object} workerData - Updated worker data
 * @returns {Promise} API response
 */
export const updateWorkerInRoster = async (rosterId, workerId, workerData) => {
  try {
    const response = await getApiData(
      `projects/rosters/${rosterId}/workers/${workerId}`,
      "PUT",
      workerData,
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Remove worker from roster
 * @param {number} rosterId - The roster ID
 * @param {number} workerId - The worker ID to remove
 * @returns {Promise} API response (204 No Content)
 */
export const deleteWorkerFromRoster = async (rosterId, workerId) => {
  try {
    const response = await getApiData(
      `projects/rosters/${rosterId}/workers/${workerId}`,
      "DELETE",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update roster (Only in Draft Status)
 * @param {number} rosterId - The roster ID
 * @param {string} rosterName - Updated roster name
 * @returns {Promise} API response
 */
export const updateRoster = async (rosterId, rosterName) => {
  try {
    // Get contractor ID from token
    const { auth: { token } } = store.getState();
    const contractorId = getContractorId(token);

    const response = await getApiData(
      `projects/rosters/${rosterId}`,
      "PUT",
      { rosterName, contractorId },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Submit roster for approval
 * @param {number} rosterId - The roster ID
 * @returns {Promise} API response
 */
export const submitRosterForApproval = async (rosterId) => {
  try {
    const response = await getApiData(
      `projects/rosters/${rosterId}/status`,
      "PATCH",
      { status: "SUBMITTED" },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Revert roster to draft status (when contractor makes changes to approved roster)
 * @param {number} rosterId - The roster ID
 * @returns {Promise} API response
 */
export const revertRosterToDraft = async (rosterId) => {
  try {
    const response = await getApiData(
      `api/projects/rosters/${rosterId}/status`,
      "PATCH",
      { status: "DRAFT" },
      { "Content-Type": "application/json" },
      false // Use REACT_APP_BASE_URL (Node.js API)
    );
    return response;
  } catch (error) {
    throw error;
  }
};


/**
 * Get all attendance sheets for a roster
 * @param {number} rosterId - The roster ID
 * @param {Object} filters - Optional filters (status, startDate, endDate)
 * @returns {Promise} API response with attendance sheets list
 */
export const getAllAttendanceSheets = async (rosterId, filters = {}) => {
  try {
    let endpoint = `rosters/${rosterId}/attendance-sheets`
    
    // Build query string from filters
    const queryParams = []
    if (filters.status) queryParams.push(`status=${filters.status}`)
    if (filters.startDate) queryParams.push(`startDate=${filters.startDate}`)
    if (filters.endDate) queryParams.push(`endDate=${filters.endDate}`)
    
    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join("&")}`
    }
    
    const response = await getApiData(
      endpoint,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get single attendance sheet details
 * @param {number} attendanceSheetId - The attendance sheet ID
 * @returns {Promise} API response with attendance sheet details
 */
export const getAttendanceSheet = async (attendanceSheetId) => {
  try {
    const response = await getApiData(
      `attendance-sheets/${attendanceSheetId}`,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Create weekly attendance sheet
 * @param {number} rosterId - The roster ID
 * @param {string} weekStartDate - Week start date (YYYY-MM-DD)
 * @param {string} weekEndDate - Week end date (YYYY-MM-DD)
 * @returns {Promise} API response
 */
export const createAttendanceSheet = async (rosterId, weekStartDate, weekEndDate) => {
  try {
    const response = await getApiData(
      `rosters/${rosterId}/attendance-sheets`,
      "POST",
      {
        weekStartDate,
        weekEndDate,
        rosterId,
        status: "DRAFT",
      },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Share attendance sheet with admin (submit for approval)
 * @param {number} attendanceSheetId - The attendance sheet ID
 * @returns {Promise} API response
 */
export const shareAttendanceSheetWithAdmin = async (attendanceSheetId) => {
  try {
    const response = await getApiData(
      `attendance-sheets/${attendanceSheetId}/status`,
      "PATCH",
      {
        status: "SUBMITTED",
      },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update planned attendance (bulk update)
 * @param {number} attendanceSheetId - The attendance sheet ID
 * @param {Array} attendanceRecords - Array of attendance records to update
 * @returns {Promise} API response
 */
export const updateAttendanceRecordsBulk = async (attendanceSheetId, attendanceRecords) => {
  try {
    const response = await getApiData(
      `attendance-sheets/${attendanceSheetId}/records`,
      "PUT",
      {
        attendanceRecords,
      },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Update single attendance record
 * @param {number} attendanceRecordId - The attendance record ID
 * @param {string} attendanceType - Attendance type (FullDay, HalfDay, Absent)
 * @param {string} remarks - Optional remarks
 * @returns {Promise} API response
 */
export const updateAttendanceRecord = async (attendanceRecordId, attendanceType, remarks = null) => {
  try {
    const response = await getApiData(
      `attendance-records/${attendanceRecordId}`,
      "PATCH",
      {
        attendanceType,
        remarks,
      },
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Search workers
 * @param {string} query - Worker name or worker code
 * @param {number} projectId - Optional project ID to filter by
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Page size (default: 20)
 * @returns {Promise} API response
 */
export const searchWorkers = async (query, projectId = null, page = 1, pageSize = 20) => {
  try {
    let endpoint = `workers/search?query=${encodeURIComponent(query)}&page=${page}&pageSize=${pageSize}`
    if (projectId) {
      endpoint += `&projectId=${projectId}`
    }
    
    const response = await getApiData(
      endpoint,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get worker full schedule
 * @param {number} workerId - The worker ID
 * @param {string} startDate - Optional start date filter (YYYY-MM-DD)
 * @param {string} endDate - Optional end date filter (YYYY-MM-DD)
 * @param {number} projectId - Optional project ID to filter by
 * @returns {Promise} API response
 */
export const getWorkerSchedule = async (workerId, startDate = null, endDate = null, projectId = null) => {
  try {
    let endpoint = `workers/${workerId}/schedule`
    const queryParams = []
    
    if (startDate) queryParams.push(`startDate=${startDate}`)
    if (endDate) queryParams.push(`endDate=${endDate}`)
    if (projectId) queryParams.push(`projectId=${projectId}`)
    
    if (queryParams.length > 0) {
      endpoint += `?${queryParams.join("&")}`
    }
    
    const response = await getApiData(
      endpoint,
      "GET",
      {},
      { "Content-Type": "application/json" },
      true // Use REACT_APP_NEW_BASE_URL
    );
    return response;
  } catch (error) {
    throw error;
  }
};

